import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { GraduationCandidates } from '../../../../lib/api/graduation-candidates.api';
import { errorText } from '../../../../lib/error-text';
import { formatDate } from '../../../../lib/i18n-format';
import { openGeneratedPdf } from '../../../../lib/open-generated-pdf';
import { renderWithoutHyphenation } from '../../../../lib/pdf-hyphenation';
import { useTenantStore } from '../../../../stores/tenant.store';
import {
  buildCandidatesReport,
  type CandidatesReport,
  type CandidatesView,
} from './candidates-report.model';
import { buildCandidatesSlides } from './candidates-slides.model';
import type { SlidesLabels } from './CandidatesSlidesPdf';
import type { CandidatesLetterLabels } from './CandidatesLetterPdf';

// ─── Candidatos: armar y abrir las diapositivas o el informe carta ──────────
// Con el MISMO modelo que la pestaña (candidates-report): una vista, el mismo
// total en el resumen, la distribución y la lista. La pestaña nueva se abre
// dentro del clic (openGeneratedPdf); después de los await, el navegador la
// bloquearía en silencio.

export function useCandidatesExports(data: GraduationCandidates, near: number) {
  const { t } = useTranslation('sis');
  const { currentTenantId } = useTenantStore();
  const [error, setError] = useState<string | null>(null);
  const c = (key: string, o?: Record<string, unknown>): string =>
    t(`reports.candidates.${key}`, o ?? {});

  /** Los textos que comparten las diapositivas y la carta. */
  const common = (r: CandidatesReport) => ({
    title: c('title'),
    viewLabel: c(`views.${r.view}`),
    viewRule: c(`viewRule.${r.view}`, { count: r.recentYears }),
    nearRule: c('nearRule', { count: r.near }),
    eligible: c('eligible'),
    near: c('near'),
    total: c('total'),
    evaluated: c('evaluated'),
    evaluatedRule: c('evaluatedRule'),
    summaryTitle: c('summaryTitle'),
    distributionTitle: c('distributionTitle'),
    totalLine: (n: number) => c('totalLine', { count: n }),
    missingLabel: (n: number) => (n === 0 ? c('eligible') : c('missingCourses', { count: n })),
    groupTitle: (missing: number, count: number) =>
      `${missing === 0 ? c('eligible') : c('missingCourses', { count: missing })} · ${c('subtotal', { count })}`,
    colN: c('columns.n'),
    colName: c('columns.student'),
    colCode: c('columns.studentCode'),
    colProgram: c('columns.program'),
    colMissing: c('columns.missing'),
    colLastTerm: c('columns.lastTerm'),
    dataAsOf: c('dataAsOf', { date: formatDate(new Date(), { dateStyle: 'long' }) }),
  });

  const download = async (opts: { includeNames: boolean; view: CandidatesView }) => {
    setError(null);
    try {
      await openGeneratedPdf(
        async () => {
          const [{ pdf }, { CandidatesSlidesPdf }, { loadPdfBrand }] = await Promise.all([
            import('@react-pdf/renderer'),
            import('./CandidatesSlidesPdf'),
            import('../analytics/pdf/use-analytics-export'),
          ]);
          const brand = await loadPdfBrand(currentTenantId!);
          const report = buildCandidatesReport(data, { view: opts.view, near });
          const slides = buildCandidatesSlides(report, { includeNames: opts.includeNames });
          const programs = report.rules.map((r) => r.name);
          const L: SlidesLabels = {
            ...common(report),
            programs: c('programs', { count: programs.length, list: programs.join(', ') }),
            namesTitle: (page, pages) => c('slides.namesTitle', { page, pages }),
            personalData: c('slides.personalData'),
            page: (n, total) => c('slides.page', { n, total }),
          };
          return renderWithoutHyphenation(() =>
            pdf(<CandidatesSlidesPdf data={slides} brand={brand} L={L} />).toBlob(),
          );
        },
        c(`slides.file.${opts.view}`),
      );
    } catch (e) {
      setError(errorText(e, c('slides.error')));
    }
  };

  /** Informe carta CON nombres de la vista elegida: solo gestión (la pestaña ya
   *  lo es), con «Contiene datos personales · uso interno» en cada página. */
  const downloadLetter = async (view: CandidatesView) => {
    setError(null);
    try {
      await openGeneratedPdf(
        async () => {
          const [{ pdf }, { CandidatesLetterPdf }, { loadPdfBrand }] = await Promise.all([
            import('@react-pdf/renderer'),
            import('./CandidatesLetterPdf'),
            import('../analytics/pdf/use-analytics-export'),
          ]);
          const brand = await loadPdfBrand(currentTenantId!);
          const report = buildCandidatesReport(data, { view, near });
          const base = common(report);
          const L: CandidatesLetterLabels = {
            ...base,
            sub: brand.tenantName,
            snapshot: base.dataAsOf,
            pii: c('letter.pii'),
            page: (n, total) => t('reports.analytics.export.page', { n, total }),
            rulesTitle: c('rules.title'),
            program: (name) => c('rules.program', { name }),
            required: (count) => c('rules.required', { count }),
            core: (min) => c('rules.core', { count: min }),
            credits: (min, per) => c('rules.credits', { min, per }),
            namesTitle: c('letter.namesTitle', { view: base.viewLabel }),
            detailRequired: (list) => c('detail.required', { list }),
            detailCore: (n) => c('detail.core', { count: n }),
            detailElectives: (n) => c('detail.electives', { count: n }),
          };
          return renderWithoutHyphenation(() =>
            pdf(<CandidatesLetterPdf report={report} brand={brand} L={L} />).toBlob(),
          );
        },
        c(`letter.file.${view}`),
      );
    } catch (e) {
      setError(errorText(e, c('slides.error')));
    }
  };

  return { download, downloadLetter, error };
}
